/**
 * OrgAdvancedFilterDialog
 * Multi-criteria, year-aware filter for the Organisations screen.
 * Users pick a year, add any number of {table, field, op, value} rules,
 * AND-combined. Also supports saving/loading named presets.
 */
import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Filter,
  Plus,
  X,
  Save,
  Bookmark,
  Trash2,
  Loader2,
} from 'lucide-react';
import { authFetch, authJsonFetch } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const OPS = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'set', label: 'Has a value' },
  { value: 'empty', label: 'Is empty' },
];

// Operators shown when the selected field is a DATE-type field. The "month"
// op matches any row whose date falls within the selected month of the
// dialog's top-level year.
const DATE_OPS = [
  { value: 'month', label: 'In month' },
  { value: 'set', label: 'Has a value' },
  { value: 'empty', label: 'Is empty' },
];

const MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

/**
 * Detect whether a field is date-like. Uses schema `type` first (may be set
 * by the backend for properly-typed columns) then falls back to a name
 * pattern because most app dates are stored as ISO strings with type=string.
 */
const isDateLikeField = (schemaFieldType, fieldName) => {
  const t = (schemaFieldType || '').toLowerCase();
  if (t === 'date' || t === 'datetime' || t === 'timestamp') return true;
  const n = (fieldName || '').toLowerCase();
  if (!n) return false;
  return (
    n.includes('date')
    || n.endsWith('_iso')
    || n.includes('timestamp')
    || n === 'dob'
    || n === 'birthday'
  );
};

const needsValue = (op) => op === 'equals' || op === 'contains';

const makeRule = () => ({
  id: `r_${Math.random().toString(36).slice(2, 10)}`,
  table: '',
  field: '',
  op: 'contains',
  value: '',
  fieldSearch: '', // autocomplete input text
  fieldType: '', // cached schema type of the selected field
});

const OrgAdvancedFilterDialog = memo(({ open, onOpenChange, onApply, currentYear }) => {
  const [year, setYear] = useState(currentYear || new Date().getFullYear());
  const [rules, setRules] = useState([makeRule()]);
  const [schema, setSchema] = useState({}); // { [table]: { label, fields: {[field]: {label, ...}} } }
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  // Saved-presets
  const [savedList, setSavedList] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);
  const [saving, setSaving] = useState(false);

  const yearOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    const years = [];
    for (let y = cy + 1; y >= cy - 6; y--) years.push(y);
    return years;
  }, []);

  // Years that actually have data — fetched once on mount. Used to default
  // the dialog's Year selector to the most-recent year with data, so date-
  // field month filters return real results out-of-the-box (the previous
  // default was current-calendar-year which often had 0 records → confused
  // operators thinking the filter was broken).
  const [yearsWithData, setYearsWithData] = useState([]);
  const fetchYearsWithData = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/years`);
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        setYearsWithData(data);
      }
    } catch (_e) { /* noop */ }
  }, []);

  // Flatten schema into a list of searchable {table, field, tableLabel, fieldLabel, fieldType}
  const allFieldOptions = useMemo(() => {
    const out = [];
    Object.entries(schema).forEach(([tableName, tableData]) => {
      const tLabel = tableData?.label || tableName;
      Object.entries(tableData?.fields || {}).forEach(([fieldName, fieldData]) => {
        out.push({
          table: tableName,
          field: fieldName,
          tableLabel: tLabel,
          fieldLabel: fieldData?.label || fieldName,
          fieldType: fieldData?.type || '',
          searchKey: `${tLabel} ${fieldData?.label || fieldName} ${tableName} ${fieldName}`.toLowerCase(),
        });
      });
    });
    return out;
  }, [schema]);

  const fetchSchema = useCallback(async () => {
    setSchemaLoading(true);
    try {
      const res = await authFetch(`${API}/export-data/schema`);
      if (res.ok) {
        const data = await res.json();
        // Backend returns { schema: { table: { label, fields: { ... } } } }
        setSchema(data.schema || data.tables || data || {});
      }
    } catch (_e) {
      // ignore
    } finally {
      setSchemaLoading(false);
    }
  }, []);

  const fetchSaved = useCallback(async () => {
    setSavedLoading(true);
    try {
      const res = await authFetch(`${API}/organizations/advanced-filter/saved`);
      if (res.ok) {
        const data = await res.json();
        setSavedList(Array.isArray(data) ? data : []);
      }
    } catch (_e) {
      // ignore
    } finally {
      setSavedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchSchema();
      fetchSaved();
      fetchYearsWithData();
      // Reset year to a sensible default each time the dialog opens.
      // We start with current calendar year; the next effect refines it to
      // the most-recent year with data once /years has resolved.
      setYear(currentYear || new Date().getFullYear());
    }
  }, [open, currentYear, fetchSchema, fetchSaved, fetchYearsWithData]);

  // Refine year default once we know which years actually have data.
  // Only fires when the dialog is open AND the selector is still on the
  // initial current-calendar-year — never overrides a user-changed selection.
  useEffect(() => {
    if (!open || yearsWithData.length === 0) return;
    const cy = new Date().getFullYear();
    if (yearsWithData.includes(cy)) return; // current year has data, leave it
    setYear(prev => (prev === cy ? yearsWithData[0] : prev));
  }, [open, yearsWithData]);

  // Helper: returns true if any rule is filtering a date-like field with op=month
  const hasDateMonthRule = useMemo(
    () => rules.some(r => r.field && isDateLikeField(r.fieldType, r.field) && r.op === 'month'),
    [rules]
  );

  // When a date-month rule is active, suggest a year that actually has data
  // for date-keyed records. Picks the most recent year EXCLUDING the
  // current calendar year if the data's "real" year is older. Shown as a
  // helper hint next to the year dropdown so operators don't get 0-result
  // surprises.
  const suggestedYear = useMemo(() => {
    if (!hasDateMonthRule || yearsWithData.length === 0) return null;
    const cy = new Date().getFullYear();
    // If current-year is in the list but isn't the most-recent-with-data,
    // suggest the most recent (which is always at index 0 of yearsWithData
    // since the backend returns it sorted DESC).
    const mostRecent = yearsWithData[0];
    if (year === cy && mostRecent !== cy) return mostRecent;
    return null;
  }, [hasDateMonthRule, yearsWithData, year]);

  const updateRule = (id, patch) => {
    setRules(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRule = (id) => {
    setRules(prev => (prev.length <= 1 ? [makeRule()] : prev.filter(r => r.id !== id)));
  };

  const addRule = () => setRules(prev => [...prev, makeRule()]);

  const handleApply = async () => {
    // Only send rules that have a field picked
    const payloadFilters = rules
      .filter(r => r.table && r.field)
      .map(r => ({
        table: r.table,
        field: r.field,
        op: r.op || 'set',
        value: (needsValue(r.op) || r.op === 'month') ? (r.value || '') : undefined,
      }));

    if (payloadFilters.length === 0) {
      toast.error('Add at least one filter with a selected field');
      return;
    }

    setApplying(true);
    try {
      const res = await authJsonFetch(`${API}/organizations/advanced-filter`, {
        method: 'POST',
        body: JSON.stringify({ year, filters: payloadFilters }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Filter failed');
        return;
      }
      const data = await res.json();
      onApply({
        org_ids: data.org_ids || [],
        year,
        filters: payloadFilters,
        match_count: data.match_count ?? (data.org_ids || []).length,
      });
      onOpenChange(false);
      toast.success(`${data.match_count ?? (data.org_ids || []).length} organisation(s) matched`);
    } catch (_e) {
      toast.error('Failed to apply filter');
    } finally {
      setApplying(false);
    }
  };

  const handleSavePreset = async () => {
    const payloadFilters = rules
      .filter(r => r.table && r.field)
      .map(r => ({ table: r.table, field: r.field, op: r.op || 'set', value: (needsValue(r.op) || r.op === 'month') ? (r.value || '') : undefined }));
    if (!saveName.trim() || payloadFilters.length === 0) {
      toast.error('Name and at least one valid rule required');
      return;
    }
    setSaving(true);
    try {
      const res = await authJsonFetch(`${API}/organizations/advanced-filter/saved`, {
        method: 'POST',
        body: JSON.stringify({
          name: saveName.trim(),
          year,
          filters: payloadFilters,
          is_shared: saveShared,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save filter');
        return;
      }
      toast.success(`Saved "${saveName.trim()}"`);
      setShowSavePrompt(false);
      setSaveName('');
      fetchSaved();
    } catch (_e) {
      toast.error('Failed to save filter');
    } finally {
      setSaving(false);
    }
  };

  const loadSaved = (preset) => {
    setYear(preset.year || currentYear || new Date().getFullYear());
    const loaded = (preset.filters || []).map((f) => ({
      id: `r_${Math.random().toString(36).slice(2, 10)}`,
      table: f.table,
      field: f.field,
      op: f.op || 'set',
      value: f.value || '',
      fieldSearch: '',
    }));
    setRules(loaded.length > 0 ? loaded : [makeRule()]);
    toast.info(`Loaded "${preset.name}"`);
  };

  const deleteSaved = async (preset) => {
    if (!window.confirm(`Delete saved filter "${preset.name}"?`)) return;
    try {
      const res = await authFetch(`${API}/organizations/advanced-filter/saved/${preset.filter_id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Deleted');
        fetchSaved();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Delete failed');
      }
    } catch (_e) {
      toast.error('Delete failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto" data-testid="org-advanced-filter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" /> Advanced Filter
          </DialogTitle>
          <DialogDescription>
            Year-aware multi-criteria filter. All rules are combined with AND.
          </DialogDescription>
        </DialogHeader>

        {/* Year + saved presets */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          <div className="flex-shrink-0">
            <label className="text-xs text-muted-foreground block mb-1">Year</label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-32" data-testid="adv-filter-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {suggestedYear && (
              <button
                type="button"
                onClick={() => setYear(suggestedYear)}
                className="text-[11px] text-amber-700 dark:text-amber-300 mt-1 hover:underline"
                data-testid="adv-filter-year-suggestion"
              >
                Tip: {suggestedYear} has data — switch?
              </button>
            )}
          </div>

          <div className="flex-1 w-full">
            <label className="text-xs text-muted-foreground block mb-1">Saved filters</label>
            {savedLoading ? (
              <div className="text-sm text-muted-foreground"><Loader2 className="inline w-3 h-3 animate-spin mr-1" />Loading…</div>
            ) : savedList.length === 0 ? (
              <div className="text-sm text-muted-foreground">No saved filters yet.</div>
            ) : (
              <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto">
                {savedList.map(p => (
                  <div key={p.filter_id} className="flex items-center gap-1">
                    <Badge
                      variant="secondary"
                      className="cursor-pointer hover:bg-primary/20"
                      onClick={() => loadSaved(p)}
                      data-testid={`adv-filter-saved-${p.filter_id}`}
                    >
                      <Bookmark className="w-3 h-3 mr-1" />
                      {p.name}{p.is_shared ? ' (shared)' : ''}
                    </Badge>
                    <button
                      type="button"
                      onClick={() => deleteSaved(p)}
                      className="text-muted-foreground hover:text-destructive"
                      title="Delete saved filter"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Rules */}
        <div className="space-y-2 border-t pt-3" data-testid="adv-filter-rules">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Filter rules</span>
            <Button size="sm" variant="outline" onClick={addRule} data-testid="adv-filter-add-rule">
              <Plus className="w-3 h-3 mr-1" /> Add rule
            </Button>
          </div>

          {schemaLoading && <div className="text-xs text-muted-foreground"><Loader2 className="inline w-3 h-3 animate-spin mr-1" />Loading fields…</div>}

          {rules.map((rule, idx) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              idx={idx}
              fieldOptions={allFieldOptions}
              onChange={(patch) => updateRule(rule.id, patch)}
              onRemove={() => removeRule(rule.id)}
              canRemove={rules.length > 1}
            />
          ))}
        </div>

        {/* Save preset */}
        {showSavePrompt && (
          <div className="border rounded p-3 space-y-2">
            <div className="text-sm font-medium">Save this filter as a preset</div>
            <Input
              placeholder="Name (e.g. '2025 Photographer John')"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              data-testid="adv-filter-save-name"
            />
            <div className="flex items-center gap-2">
              <Checkbox id="adv-filter-shared" checked={saveShared} onCheckedChange={(v) => setSaveShared(!!v)} />
              <label htmlFor="adv-filter-shared" className="text-sm cursor-pointer">Share with other users</label>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowSavePrompt(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSavePreset} disabled={saving} data-testid="adv-filter-save-confirm">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Save
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setShowSavePrompt(s => !s)}
            data-testid="adv-filter-save-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {showSavePrompt ? 'Hide save' : 'Save filter'}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleApply} disabled={applying} data-testid="adv-filter-apply-btn">
              {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Filter className="w-4 h-4 mr-1" />}
              Apply Filter
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

OrgAdvancedFilterDialog.displayName = 'OrgAdvancedFilterDialog';

// ---------------- RuleRow with field-autocomplete ----------------
const RuleRow = memo(({ rule, idx, fieldOptions, onChange, onRemove, canRemove }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filteredOptions = useMemo(() => {
    const q = (rule.fieldSearch || '').trim().toLowerCase();
    if (!q) return fieldOptions.slice(0, 50);
    return fieldOptions
      .filter(o => o.searchKey.includes(q))
      .slice(0, 50);
  }, [rule.fieldSearch, fieldOptions]);

  const selectedLabel = rule.table && rule.field
    ? `${(fieldOptions.find(o => o.table === rule.table && o.field === rule.field)?.tableLabel) || rule.table} — ${(fieldOptions.find(o => o.table === rule.table && o.field === rule.field)?.fieldLabel) || rule.field}`
    : '';

  const isDateField = isDateLikeField(rule.fieldType, rule.field);
  const opsForField = isDateField ? DATE_OPS : OPS;

  return (
    <div className="flex flex-col md:flex-row gap-2 items-start border rounded p-2" data-testid={`adv-filter-rule-${idx}`}>
      {/* Field search (autocomplete) */}
      <div className="flex-1 w-full relative">
        <Input
          placeholder="Type to search tables & fields…"
          value={rule.fieldSearch || selectedLabel}
          onChange={(e) => { onChange({ fieldSearch: e.target.value, table: '', field: '', fieldType: '' }); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          data-testid={`adv-filter-field-input-${idx}`}
        />
        {showSuggestions && filteredOptions.length > 0 && (
          <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-background border rounded-md shadow-lg max-h-64 overflow-y-auto">
            {filteredOptions.map((o) => (
              <button
                key={`${o.table}__${o.field}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const dateLike = isDateLikeField(o.fieldType, o.field);
                  onChange({
                    table: o.table,
                    field: o.field,
                    fieldType: o.fieldType || '',
                    fieldSearch: `${o.tableLabel} — ${o.fieldLabel}`,
                    // Auto-switch the operator sensibly when the user picks a
                    // date field (default to month selection) vs a normal
                    // field (keep current op if valid).
                    op: dateLike ? 'month' : (rule.op && ['contains', 'equals', 'set', 'empty'].includes(rule.op) ? rule.op : 'contains'),
                    value: dateLike ? String(new Date().getMonth() + 1) : (rule.value || ''),
                  });
                  setShowSuggestions(false);
                }}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm border-b last:border-b-0 hover:bg-muted/60',
                  rule.table === o.table && rule.field === o.field && 'bg-primary/10 font-medium'
                )}
                data-testid={`adv-filter-field-option-${o.table}-${o.field}`}
              >
                <span className="text-xs text-muted-foreground">{o.tableLabel}</span>
                <span className="mx-2 text-muted-foreground">›</span>
                <span>{o.fieldLabel}</span>
                {isDateLikeField(o.fieldType, o.field) && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-primary font-medium">date</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Op */}
      <Select value={rule.op} onValueChange={(v) => onChange({ op: v })}>
        <SelectTrigger className="w-32" data-testid={`adv-filter-op-${idx}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opsForField.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>

      {/* Value / month picker */}
      {isDateField && rule.op === 'month' ? (
        <div className="flex items-center gap-1">
          <Select
            value={rule.value || String(new Date().getMonth() + 1)}
            onValueChange={(v) => onChange({ value: v })}
          >
            <SelectTrigger className="w-36" data-testid={`adv-filter-month-${idx}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            title="Inspect how this date field is stored (helps debug 0-result filters)"
            onClick={async () => {
              try {
                const params = new URLSearchParams({ table: rule.table, field: rule.field });
                const res = await authFetch(`${API}/organizations/advanced-filter/debug-field?${params.toString()}`);
                if (!res.ok) {
                  toast.error('Debug fetch failed');
                  return;
                }
                const data = await res.json();
                const samples = (data.samples || []).map((s) => `${s.type}: ${s.value}`).join('\n');
                window.alert(
                  `Field: ${data.table}.${data.field}\n` +
                  `Total with value: ${data.total_with_value}\n\n` +
                  `Storage samples (max 10):\n${samples || '(none)'}\n\n` +
                  `If your dates show as something other than YYYY-MM-DD, the month filter now handles all formats. ` +
                  `If the type is "datetime" the filter uses a date range (also handled).`
                );
              } catch (_e) {
                toast.error('Debug fetch failed');
              }
            }}
            data-testid={`adv-filter-debug-${idx}`}
          >
            <span className="text-xs">?</span>
          </Button>
        </div>
      ) : (
        <Input
          placeholder={needsValue(rule.op) ? 'Search term…' : 'n/a for this operator'}
          className="w-44"
          value={rule.value || ''}
          onChange={(e) => onChange({ value: e.target.value })}
          disabled={!needsValue(rule.op)}
          data-testid={`adv-filter-value-${idx}`}
        />
      )}

      {/* Remove */}
      <Button
        size="icon"
        variant="ghost"
        onClick={onRemove}
        disabled={!canRemove}
        title="Remove rule"
        data-testid={`adv-filter-remove-rule-${idx}`}
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
});
RuleRow.displayName = 'RuleRow';

export default OrgAdvancedFilterDialog;
